import { Link } from "react-router-dom";
import { Star, GitBranch, LogIn } from "lucide-react";
import { useFavorites } from "../context/FavoritesContext";
import { useAuth } from "../context/AuthContext";
import { useCatalog } from "../lib/useCatalog";
import { ModelCard } from "../components/ModelCard";
import { FavoriteButton } from "../components/FavoriteButton";
import { ShareFavorites } from "../components/ShareFavorites";

export function TrendingFavorite({ item }) {
  return (
    <div data-testid="favorite-trending-card" className="flex min-w-0 flex-col rounded-xl border border-[#1E2638] bg-[#0F141C] p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono2 text-[11px] text-orange-400">À la une · {item.meta?.author}</p>
          <h3 className="truncate font-head text-base font-bold" title={item.name}>{item.name}</h3>
          {item.meta?.params && <p className="font-mono2 text-xs text-muted-foreground">{item.meta.params}B</p>}
        </div>
        <FavoriteButton item={item} />
      </div>
      <a href={`https://huggingface.co/${item.ref}`} target="_blank" rel="noopener noreferrer"
        className="mt-4 flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
        <GitBranch className="h-3.5 w-3.5" /> Voir sur Hugging Face
      </a>
    </div>
  );
}

export default function Favorites() {
  const { items } = useFavorites();
  const { user } = useAuth();
  const { data } = useCatalog();
  const catalogFavs = items.filter((i) => i.kind === "catalog").map((i) => data?.models.find((m) => m.id === i.ref)).filter(Boolean);
  const trendingFavs = items.filter((i) => i.kind === "trending");

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8" data-testid="favorites-page">
      <span className="flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-widest text-amber-400"><Star className="h-4 w-4" /> Favoris</span>
      <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight">Mes modèles épinglés</h1>
      <p className="mt-1 text-muted-foreground">Cliquez sur l'étoile d'un modèle du catalogue ou de « À la une » pour le retrouver ici.</p>

      {!user && (
        <div data-testid="favorites-guest-notice" className="mt-5 flex flex-wrap items-center gap-3 rounded-lg border border-[#1E2638] bg-[#0F141C] p-4 text-sm text-muted-foreground">
          Vos favoris sont enregistrés sur cet appareil uniquement.
          <Link to="/login" className="inline-flex items-center gap-1 text-primary hover:underline"><LogIn className="h-4 w-4" /> Connectez-vous pour les retrouver partout</Link>
        </div>
      )}

      {items.length > 0 && <ShareFavorites items={items} />}

      {items.length === 0 ? (
        <p data-testid="favorites-empty" className="mt-8 rounded-xl border border-dashed border-[#1E2638] p-8 text-center text-sm text-muted-foreground">
          Aucun favori pour l'instant. Parcourez le <Link to="/catalogue" className="text-primary hover:underline">catalogue</Link> ou les <Link to="/tendances" className="text-primary hover:underline">modèles à la une</Link>.
        </p>
      ) : (
        <div className="mt-8 space-y-8">
          {catalogFavs.length > 0 && (
            <section>
              <h2 className="mb-3 font-head text-lg font-bold">Catalogue ({catalogFavs.length})</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {catalogFavs.map((m) => <ModelCard key={m.id} model={m} quantizations={data.quantizations} />)}
              </div>
            </section>
          )}
          {trendingFavs.length > 0 && (
            <section>
              <h2 className="mb-3 font-head text-lg font-bold">À la une ({trendingFavs.length})</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {trendingFavs.map((i) => <TrendingFavorite key={i.ref} item={i} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
